import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, QueryList, AfterContentInit, TemplateRef } from '@angular/core';

import { TableConfig } from '../../../util/table-config.utils';
import { TableColumnComponent } from '../components/column/table-column.component';

import { Subject, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import * as _ from 'lodash';
import { Log } from "ng2-logger";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";

@Component({
    selector: 'app-table-header',
    templateUrl: 'table-header.component.html',
    styleUrls: ['table-header.component.scss']
})
export class TableHeaderComponent implements OnInit, OnDestroy, AfterContentInit {
    _logger = Log.create(TableHeaderComponent.name);

    @Input() columns: QueryList<TableColumnComponent>;
    @Input() title;
    @Input() config = new TableConfig();
    @Input() rowExpand = false;
    @Input() allExpanded = false;
    @Input() filtersToggle = true;
    @Input() showFilters = true;
    @Input() showGroupFilters = false;
    @Input() showGroupers = false;
    @Input() displayToolsUsageLegend = false;
    @Input() directlyGrouped = false;
    @Input() hasTitle = true;
    @Input() hasToggleFilters = true;
    @Input() saveFilters = false;
    @Input() backgroundColor: string;
    @Input() parentComponent: string;
    @Input() isFilterExpanded = false;
    @Input() childTemplate: TemplateRef<any>;
    @Input() definedGroupers: boolean;
    @Input() hasSelectALl: boolean;
    @Input() isSelectAllChecked: boolean;

    @Output() sort = new EventEmitter<any>();
    @Output() filter = new EventEmitter<any>();
    @Output() filterToggled = new EventEmitter<any>();
    @Output() groupFilter = new EventEmitter<any>();
    @Output() group = new EventEmitter<any>();
    @Output() expandAll = new EventEmitter<any>();
    @Output() onFilterToggle = new EventEmitter<any>();
    @Output() lineIdFilter = new EventEmitter<any>();
    @Output() selectAll = new EventEmitter<any>();
    @Output() unSelectAll = new EventEmitter<any>();

    private filterSubject = new Subject<any>();
    private onInit = true;
    private onInitTime = 2000;
    private onModelUpdateTime = 150
    private debounceTime = 500;

    firstFilterToggle = false;
    secondFilterToggle = false;

    onFilterChange = _.debounce(this.filterChange, 300);

    templateContext = this;

    groupFilterItems = Array<any>();

    groupableDropdownItems = Array<any>();
    previousGroupers = [];

    addGroupActive = false;
    filterSubscription: Subscription;

    toolFilteringForm: UntypedFormGroup;

    @Input()
    set groupableColumns(columns) {
        if (columns.length) {
            this.groupableDropdownItems = columns
        }
    }
    @Input()
    set groupFilters(filters) {
        if (filters.length && filters.every(x => x.items.length)) {
            this.groupFilterItems = filters;
        }
    }


    constructor(private fb: UntypedFormBuilder) {
        this.toolFilteringForm = fb.group({
            firstFilter: [''],
            secondFilter: [''],
        });
    }

    ngOnInit() {
        this.registerFilterSubscription();
        if (this.showGroupers && this.directlyGrouped) {
            this.config.groupers = [...this.groupableDropdownItems];
        }
        setTimeout(() => {
            this.onSessionFilterInit();
        });
    }

    ngAfterContentInit() {
        setTimeout(() => {
            this.onInit = false;
        }, this.onInitTime)
    }

    private registerFilterSubscription() {
        this._logger.info('registerFilterSubscription::');
        this.filterSubscription = this.filterSubject
            .pipe(
                debounceTime(this.debounceTime)
            )
            .subscribe(val => {
                this.onColumnFilterChanged();
            });
    }

    ngOnDestroy() {
        this.filterSubscription.unsubscribe();
    }

    onToggleExpandAll() {
        this.expandAll.emit(!this.allExpanded);
    }

    toggleGroupFilters() {
        this.isFilterExpanded = !this.isFilterExpanded;
    }

    toggleFilters() {
        this.showFilters = !this.showFilters;
        this.filterToggled.emit(this.showFilters);
    }

    onToggleFilter(event, type) {
        this.onFilterToggle.emit({event, type});
    }

    filterChange(column) {
        column.onFilterChange();
        this.filterSubject.next(column.filterValue);
    }

    onSessionFilterInit() {
        const storedFilters = this.getSessionStorageFilters();
        if (storedFilters && storedFilters.length !== 0) {
            this.config.filters = storedFilters;
            this._logger.info('onSessionFilterInit:: Session filters are', this.config.filters);
            if (this.showFilters) {
                this.columns.forEach(col => {
                    const savedFilter = storedFilters.find(filter => filter.field === col.field);
                    if (savedFilter) {
                        col.filterValue = savedFilter.value;
                    }
                });
            }
            if (this.showGroupFilters) {
                this.groupFilterItems.forEach(groupFilter => {
                    const savedFilter = storedFilters.find(filter => filter.field === groupFilter.filterParam);
                    if (savedFilter) {
                        groupFilter.selectedValue = savedFilter.value;
                    }
                });
            }
        }
        this.groupFilter.emit(this.config);
    }

    // Get table filters from Session Storage
    getSessionStorageFilters() {
        return JSON.parse(sessionStorage.getItem(this.parentComponent + '.table.filters'));
    }

    onColumnFilterChanged() {
        if (this.showFilters) {
            this.config.filters = this.getActiveFilters();
            this._logger.info('onColumnFilterChanged:: Active filters are', this.config.filters);
            this.filter.emit(this.config);
        }
    }

    onGroupFilterChanged() {
        if (this.showGroupFilters && !this.onInit) {
            setTimeout(() => {
                this.config.filters = this.getActiveFilters();
                this._logger.info('onGroupFilterChanged:: Active filters are', this.config.filters);
                if (!this.onInit) {
                    this.groupFilter.emit(this.config);
                }
            }, this.onModelUpdateTime);
        }
    }

    onSort(column) {
        if (!column.header || !column.sortable) {
            return;
        }
        this.columns.forEach(col => {
            if (col.field !== column.field) {
                col.clearSorting();
            }
        });

        column.updateSort();
        this.config.sorter = {
            field: column.field,
            IsSortingAscending: column.sort.direction === 'asc'
        };
        this.sort.emit(this.config);
    }

    getActiveFilters() {
        const filters = _.union(this.getColumnFilters(), this.getGroupFilters());
        if (this.saveFilters) {
            sessionStorage.setItem(this.parentComponent + '.table.filters', JSON.stringify(filters));
        }
        return filters;
    }

    getColumnFilters() {
        const filters = [];
        this.columns.forEach(col => {
            if (col.filterValue !== null && col.filterValue !== undefined && col.filterValue !== '') {
                filters.push({
                    field: col.field,
                    value: col.filterValue
                })
            }
        });
        return filters;
    }

    getGroupFilters() {
        const filters = [];
        this.groupFilterItems.forEach(groupFilter => {
            if (groupFilter.selectedValue && groupFilter.selectedValue.id === "" && groupFilter.filterParam === 'lineId') {
                this.lineIdFilter.emit(undefined);
                groupFilter.selectedValue = undefined;

                filters.push({
                    field: groupFilter.filterParam,
                    value: groupFilter.selectedValue
                })
            }
            if (groupFilter.selectedValue) {
                // TODO: FIX LINE FILTERING FOR ALL LINES !
                if (groupFilter.filterParam === "lineId") {
                    this.lineIdFilter.emit(groupFilter.selectedValue.id);
                }

                filters.push({
                    field: groupFilter.filterParam,
                    value: groupFilter.selectedValue.id === undefined ? groupFilter.selectedValue : groupFilter.selectedValue.id
                })
            }
        });
        return filters;
    }

    onResetFilters() {
        this.config.filters = [];
        this._logger.info('onResetFilters:: Filters have been reset');
        if (this.showFilters) {
            this.columns.forEach(col => col.filterValue = null);
            this.filter.emit(this.config);
        }
        if (this.showGroupFilters) {
            this.groupFilterItems.forEach(groupFilter => groupFilter.selectedValue = null);
            this.groupFilter.emit(this.config);
        }
    }

    onAddGroupButtonClick() {
        this.addGroupActive = true;
    }

    addGrouper(grouper) {
        this.config.groupers.push(grouper);
        _.remove(this.groupableDropdownItems, (actGrouper) => grouper.id === actGrouper.id);
        this.addGroupActive = false;
        this.group.emit(this.config);
    }

    removeGrouper(grouper) {
        _.remove(this.config.groupers, (actGrouper) => grouper.id === actGrouper.id);
        this.groupableDropdownItems.push(grouper);
        this.group.emit(this.config);
    }

    isActiveGrouper(grouper) {
        return this.config.groupers.filter(x => x.value === grouper.value).length > 0;
    }

    addChipsGrouper(grouper) {
        this.config.groupers.push(grouper);
        this.group.emit(this.config);
    }

    removeChipsGrouper(grouper) {
        _.remove(this.config.groupers, (actGrouper) => grouper.id === actGrouper.id);
        this.group.emit(this.config);
    }

    onClickSelectALl(checkbox: any) {
        if (checkbox.target.checked) {
            this.selectAll.emit();
            this.isSelectAllChecked = true;
        } else {
            this.unSelectAll.emit();
            this.isSelectAllChecked = false;
        }
    }

    get showExpandAll() {
        return this.rowExpand || this.showGroupers;
    }
}
